import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaPlayCircle, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import useUser from '../../hooks/useUser'; // UNIFIED AUTH: Use unified authentication hook
import './VideoPage.css';

const VideoPage = () => {
  const { isAuthenticated, getApiToken } = useUser(); // UNIFIED AUTH: Use unified authentication

  // Mark video as watched when component mounts
  useEffect(() => {
    const markVideoWatched = async () => {
      if (isAuthenticated) {
        try {
          const token = await getApiToken(); // UNIFIED AUTH: Use unified token method
          await fetch(`${process.env.REACT_APP_API_URL}/users/onboarding-status`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ hasWatchedVideo: true })
          });
        } catch (error) {
          console.error('Error updating video status:', error);
        }
      }
    };

    // Mark as watched after 30 seconds
    const timer = setTimeout(markVideoWatched, 30000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, getApiToken]); // UNIFIED AUTH: Use unified token method

  const handleVideoEnd = async () => {
    if (isAuthenticated) {
      try {
        const token = await getApiToken(); // UNIFIED AUTH: Use unified token method
        await fetch(`${process.env.REACT_APP_API_URL}/users/onboarding-status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ hasWatchedVideo: true })
        });
      } catch (error) {
        console.error('Error updating video status:', error);
      }
    }
  };

  return (
    <div className="video-page">
      <Helmet>
        <title>BotSpot Quick Start Video | Learn the Basics</title>
        <meta name="description" content="Learn how to use BotSpot with our comprehensive quick start video tutorial." />
      </Helmet>
      
      <Container className="video-container">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {/* Back Button */}
            <div className="mb-4">
              <Button 
                as={Link}
                variant="outline-primary" 
                to="/onboarding"
                className="back-btn"
              >
                <FaArrowLeft className="me-2" />
                Back to Onboarding
              </Button>
            </div>

            {/* Video Card */}
            <Card className="video-card">
              <Card.Body>
                <div className="video-header text-center mb-4">
                  <div className="video-icon-wrapper">
                    <FaPlayCircle size={40} className="video-icon" />
                  </div>
                  <h1 className="video-title">BotSpot Quick Start</h1>
                  <p className="video-subtitle">
                    Learn the basics of BotSpot and start trading with AI in just 5 minutes
                  </p>
                </div>

                {/* Video Embed */}
                <div className="video-embed-wrapper">
                  <div className="video-embed">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/F2e-739LGps?rel=0"
                      title="BotSpot Quick Start Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      onEnded={handleVideoEnd}
                    ></iframe>
                  </div>
                </div>

                {/* Video Description */}
                <div className="video-description mt-4">
                  <h3>What You'll Learn:</h3>
                  <ul className="video-topics">
                    <li><FaCheckCircle className="me-2 text-success" />How to navigate your BotSpot dashboard</li>
                    <li><FaCheckCircle className="me-2 text-success" />Creating your first AI trading strategy</li>
                    <li><FaCheckCircle className="me-2 text-success" />Running backtests to validate performance</li>
                    <li><FaCheckCircle className="me-2 text-success" />Deploying bots for live trading</li>
                    <li><FaCheckCircle className="me-2 text-success" />Monitoring and managing your trades</li>
                  </ul>
                </div>

                {/* Next Steps */}
                <div className="video-next-steps mt-4 text-center">
                  <h4>Ready to Get Started?</h4>
                  <div className="next-steps-buttons">
                    <Button
                      as={Link}
                      variant="primary"
                      to="/ai-agent"
                      className="next-step-btn"
                    >
                      Create Your Strategy
                    </Button>
                    <Button 
                      as={Link}
                      variant="outline-primary" 
                      to="/onboarding"
                      className="next-step-btn"
                    >
                      Continue Onboarding
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VideoPage; 